import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Space, Tag, Tooltip, Typography, message } from 'antd';
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import type { Artifact } from '../types';
import {
  ArtifactDeliveryStatus,
  downloadTemporaryArtifact,
  getArtifactDelivery,
  requestProfileConnectorsNavigation,
  sendArtifactToDrive,
} from '../api/artifactDelivery';

const { Text } = Typography;

interface ArtifactDeliveryItemProps {
  artifact: Artifact;
}

interface Props {
  artifacts: Artifact[];
}

const PILL_BORDER_COLOR = '#3f3f3f';
const PILL_BACKGROUND = '#2a2a2a';

const getPillButtonStyle = (color: string): React.CSSProperties => ({
  background: PILL_BACKGROUND,
  borderColor: PILL_BORDER_COLOR,
  borderRadius: 20,
  color,
  fontSize: 11,
  cursor: 'pointer',
  pointerEvents: 'auto',
});

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

const expiryText = (expiresAt: string): string => {
  if (!expiresAt) return '';
  const expires = new Date(expiresAt).getTime();
  if (!Number.isFinite(expires)) return '';
  const diffMs = expires - Date.now();
  if (diffMs <= 0) return 'Expired';
  const mins = Math.ceil(diffMs / 60000);
  if (mins < 60) return `Expires in ${mins}m`;
  const hours = Math.ceil(mins / 60);
  if (hours < 48) return `Expires in ${hours}h`;
  return `Expires in ${Math.ceil(hours / 24)}d`;
};

const ArtifactDeliveryItem: React.FC<ArtifactDeliveryItemProps> = ({ artifact }) => {
  const [delivery, setDelivery] = useState<ArtifactDeliveryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async (forceRescan = false): Promise<ArtifactDeliveryStatus | null> => {
    setLoading(true);
    try {
      const response = await getArtifactDelivery(artifact.id, forceRescan);
      setDelivery(response.data);
      setError('');
      return response.data;
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not load artifact delivery status.';
      setError(detail);
      return null;
    } finally {
      setLoading(false);
    }
  }, [artifact.id]);

  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => void refresh(false);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh(false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  const expiresLabel = useMemo(
    () => expiryText(delivery?.temporary?.expires_at || ''),
    [delivery?.temporary?.expires_at],
  );

  const resolveDeliveryForAction = async (): Promise<ArtifactDeliveryStatus | null> => {
    if (delivery?.security?.status === 'REJECTED') {
      message.error(delivery.security.message || 'Praxys security blocked this artifact.');
      return null;
    }

    if (
      delivery?.security?.status === 'SAFE'
      && delivery?.temporary?.available
      && delivery?.temporary?.download_url
    ) {
      return delivery;
    }

    // A button click is an explicit retry point.  Re-run the scan/delivery
    // enrollment instead of presenting a permanently disabled control.
    const refreshed = await refresh(true);
    if (!refreshed) {
      message.error(error || 'Artifact delivery is not available yet.');
      return null;
    }
    if (refreshed.security?.status === 'REJECTED') {
      message.error(refreshed.security.message || 'Praxys security blocked this artifact.');
      return null;
    }
    if (refreshed.security?.status !== 'SAFE') {
      message.error(refreshed.security?.message || 'Artifact security validation has not completed successfully.');
      return null;
    }
    return refreshed;
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const current = await resolveDeliveryForAction();
      if (!current?.temporary?.available || !current.temporary.download_url) {
        message.error('A secure temporary download is not available for this artifact.');
        return;
      }
      await downloadTemporaryArtifact(current.temporary.download_url, artifact.filename);
      await refresh(false);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendToDrive = async () => {
    if (sending) return;
    setSending(true);
    try {
      const current = await resolveDeliveryForAction();
      if (!current) return;

      if (!current.google_drive?.connected) {
        requestProfileConnectorsNavigation();
        return;
      }

      const response = await sendArtifactToDrive(artifact.id);
      setDelivery(response.data.delivery);
      message.success('Saved to Google Drive under Praxys.ai/Tasks.');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Could not save artifact to Google Drive.');
    } finally {
      setSending(false);
    }
  };

  const securityStatus = delivery?.security?.status || '';
  const safe = securityStatus === 'SAFE';

  return (
    <Card
      size="small"
      style={{
        background: '#202020',
        borderColor: '#3f3f3f',
        marginBottom: 8,
      }}
      bodyStyle={{ padding: '10px 12px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <Space size={6} wrap>
            <Text style={{ color: '#e5e7eb', fontWeight: 600 }}>{artifact.filename}</Text>
            <Tag>{formatBytes(artifact.size_bytes)}</Tag>
            {safe && (
              <Tooltip title={delivery?.security?.message || 'Artifact passed Praxys security validation'}>
                <Tag color="success" icon={<SafetyCertificateOutlined />}>Security checked</Tag>
              </Tooltip>
            )}
            {securityStatus === 'REJECTED' && (
              <Tag color="error" icon={<ExclamationCircleOutlined />}>Blocked</Tag>
            )}
            {securityStatus === 'ERROR' && (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>Scan unavailable</Tag>
            )}
          </Space>
          <div style={{ marginTop: 4 }}>
            {loading ? (
              <Text type="secondary" style={{ fontSize: 11 }}>Checking artifact security and storage…</Text>
            ) : error ? (
              <Text type="danger" style={{ fontSize: 11 }}>{error}</Text>
            ) : (
              <Space size={10} wrap>
                {expiresLabel && <Text type="secondary" style={{ fontSize: 11 }}>{expiresLabel}</Text>}
                {delivery?.security?.sha256 && (
                  <Tooltip title={delivery.security.sha256}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      SHA-256 {delivery.security.sha256.slice(0, 12)}…
                    </Text>
                  </Tooltip>
                )}
              </Space>
            )}
          </div>
        </div>

        <Space size={4} wrap onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Download the temporary security-checked artifact">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                void handleDownload();
              }}
              style={getPillButtonStyle('#10b981')}
            >
              Download
            </Button>
          </Tooltip>

          {delivery?.google_drive?.uploaded ? (
            <>
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 20 }}>Saved to Drive</Tag>
              {delivery.google_drive.remote_url && (
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(delivery.google_drive.remote_url, '_blank', 'noopener,noreferrer');
                  }}
                  style={getPillButtonStyle('#38bdf8')}
                >
                  Open in Drive
                </Button>
              )}
            </>
          ) : (
            <Tooltip
              title={delivery?.google_drive?.connected
                ? 'Save this generated file to your connected Google Drive.'
                : 'Google Drive is not connected yet. Click to open Profile > Connectors.'}
            >
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
                loading={sending}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleSendToDrive();
                }}
                style={getPillButtonStyle('#38bdf8')}
              >
                Send to Drive
              </Button>
            </Tooltip>
          )}

          {(securityStatus === 'ERROR' || securityStatus === 'REJECTED') && (
            <Tooltip title="Re-run artifact security validation">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  void refresh(true);
                }}
                loading={loading}
                style={getPillButtonStyle('#faad14')}
              />
            </Tooltip>
          )}
        </Space>
      </div>
    </Card>
  );
};

const ArtifactDeliveryList: React.FC<Props> = ({ artifacts }) => {
  if (!artifacts.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <Text style={{ color: '#9ca3af', fontSize: 12, fontWeight: 600 }}>
        Downloadable artifacts
      </Text>
      <div style={{ marginTop: 6 }}>
        {artifacts.map((artifact) => (
          <ArtifactDeliveryItem key={artifact.id} artifact={artifact} />
        ))}
      </div>
    </div>
  );
};

export default ArtifactDeliveryList;
